// ============= Imports =============
import { useState, useCallback, useRef, useMemo } from 'react';
import { fetchClients } from '../../../apis/core/aderant/get/clients';
import { fetchMatters } from '../../../apis/core/aderant/get/matters';
import { UseAuth } from '../../../contexts/AuthContext';
import { createDebounce } from '@tw/tw-component-library';

// ============= Types =============
export interface Client {
  clientName: string;
  clientCode: string;
  clientUno: string;
}

export interface Matter {
  clientUno: string;
  clntMattCode: string;
  matterCode: string;
  matterName: string;
  matterUno: string;
}

export interface FormattedClientData {
  label: string;
  value: string;
  clientName: string;
  clientCode: string;
}

export interface FormattedMatterData {
  label: string;
  value: string;
  matterName: string;
  matterCode: string;
  clientUno: string;
}

export interface ApiResponse<T> {
  data: {
    items: T[];
  };
}

// ============= Hook =============
export const useClientMatterSearch = (office?: string) => {
  // Debug logging to see if the hook is being recreated
  console.log('🔍 useClientMatterSearch hook called with office:', office);

  // Memoize the office to prevent unnecessary re-renders
  const memoizedOffice = useMemo(() => office, [office]);

  // State management
  const { getAccessToken } = UseAuth();
  const [clientData, setClientData] = useState<string[]>([]);
  const [formattedClientData, setFormattedClientData] = useState<
    FormattedClientData[]
  >([]);
  const [formattedMatterData, setFormattedMatterData] = useState<
    FormattedMatterData[]
  >([]);
  const [matterData, setMatterData] = useState<string[]>([]);
  const [selectedClientUno, setSelectedClientUno] = useState<string>('');
  const [selectedMatterUno, setSelectedMatterUno] = useState<string>('');
  const [selectedClientLabel] = useState<string>('');

  const [showMatterDropdown, setShowMatterDropdown] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isLoadingMatters, setIsLoadingMatters] = useState(false);

  // Use ref to track the latest search value
  const lastSearchRefClient = useRef<string>('');
  const lastSearchRefMatter = useRef<string>('');

  // ============= Formatters =============

  const formatClientData = useCallback(
    (client: Client): FormattedClientData => ({
      label: `${client.clientName} - (${client.clientCode})`,
      value: client.clientUno,
      clientName: client.clientName,
      clientCode: client.clientCode,
    }),
    []
  );

  const formatMatterData = useCallback(
    (matter: Matter): FormattedMatterData => ({
      label: `${matter.matterName} - (${matter.clntMattCode})`,
      value: matter.matterUno,
      matterName: matter.matterName,
      matterCode: matter.matterCode,
      clientUno: matter.clientUno,
    }),
    []
  );

  // ============= Debounced Functions =============

  const debouncedClientSearch = createDebounce(async (searchValue: string) => {
    console.log('🔍 useClientMatterSearch.debouncedClientSearch called:', {
      searchValue,
    });
    console.log('🔍 About to check if search value changed');
    // Skip if the search value hasn't changed
    if (lastSearchRefClient.current === searchValue) {
      console.log('🔍 Search value unchanged, skipping');
      return;
    }
    console.log('🔍 Search value changed, proceeding');
    lastSearchRefClient.current = searchValue;

    if (!searchValue.trim()) {
      setFormattedClientData([]);
      setClientData([]);
      setIsLoadingClients(false);
      return;
    }

    try {
      setIsLoadingClients(true);
      const response = await fetchClients({
        getAccessToken,
        searchValue: searchValue,
        office: memoizedOffice,
      });
      const data = response as unknown as ApiResponse<Client>;

      const formattedData = data.data.items.map(formatClientData);
      setFormattedClientData(formattedData);
      setClientData(formattedData.map((client) => client.label));
      setShowClientDropdown(true);
      // Only set loading to false if we actually have data or if this is still the current search
      if (formattedData.length > 0) {
        setIsLoadingClients(false);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setFormattedClientData([]);
      setClientData([]);
      if (lastSearchRefClient.current === searchValue) {
        setIsLoadingClients(false);
      }
    }
  }, 300); // 300ms delay

  const debouncedMatterSearch = createDebounce(async (searchValue: string) => {
    if (lastSearchRefMatter.current === searchValue) return;
    lastSearchRefMatter.current = searchValue;

    if (!searchValue.trim()) {
      setFormattedMatterData([]);
      setMatterData([]);
      setIsLoadingMatters(false);
      return;
    }

    try {
      setIsLoadingMatters(true);
      const response = await fetchMatters({
        getAccessToken,
        searchValue: searchValue,
        office: memoizedOffice,
      });
      const data = response as unknown as ApiResponse<Matter>;

      const formattedData = data.data.items.map(formatMatterData);
      setFormattedMatterData(formattedData);
      setMatterData(formattedData.map((client) => client.label));
      setShowMatterDropdown(true);
      // Only set loading to false if this is still the current search
      if (lastSearchRefMatter.current === searchValue) {
        setIsLoadingMatters(false);
      }
    } catch (error) {
      console.error('Error fetching matters:', error);
      setFormattedMatterData([]);
      setMatterData([]);
      if (lastSearchRefMatter.current === searchValue) {
        setIsLoadingMatters(false);
      }
    }
  }, 300);

  // ============= Handlers Search =============

  const handleMatterSearch = useCallback(
    (searchValue: string) => {
      // If we have data and the search is empty, don't trigger loading
      if (!searchValue.trim() && formattedMatterData.length > 0) {
        setFormattedMatterData([]);
        setMatterData([]);
        setIsLoadingMatters(false);
        return;
      }

      debouncedMatterSearch(searchValue);
    },
    [debouncedMatterSearch, formattedMatterData.length]
  );

  const handleClientSearch = useCallback(
    (searchValue: string) => {
      console.log('🔍 useClientMatterSearch.handleClientSearch called:', {
        searchValue,
      });
      console.log('🔍 About to call debouncedClientSearch');

      // If we have data and the search is empty, don't trigger loading
      if (!searchValue.trim() && formattedClientData.length > 0) {
        setFormattedClientData([]);
        setClientData([]);
        setIsLoadingClients(false);
        return;
      }

      debouncedClientSearch(searchValue);
      console.log('🔍 debouncedClientSearch called');
    },
    [debouncedClientSearch, formattedClientData.length]
  );

  // ============= Handlers Select =============

  const handleClientSelect = useCallback(
    async (selectedOption: string) => {
      const selectedClient = formattedClientData.find(
        (client) => client.label === selectedOption
      );

      if (!selectedClient) return;

      try {
        setIsLoadingMatters(true);
        setSelectedClientUno(selectedClient.label);
        const response = await fetchMatters({
          getAccessToken,
          searchValue: selectedClient.value,
          office: memoizedOffice,
        });
        const data = response as unknown as ApiResponse<Matter>;

        const formattedData = data.data.items.map(formatMatterData);
        setFormattedMatterData(formattedData);
        setMatterData(formattedData.map((matter) => matter.label));
        // Set loading to false when we have data
        setIsLoadingMatters(false);
        setShowMatterDropdown(true);
        setShowClientDropdown(false);
      } catch (error) {
        console.error('Error fetching matters:', error);
        setFormattedMatterData([]);
        setMatterData([]);
        setIsLoadingMatters(false);
        setShowMatterDropdown(false);
      }
    },
    [formattedClientData, formatMatterData, memoizedOffice, getAccessToken]
  );

  const handleMatterSelect = useCallback(
    async (selectedOption: string) => {
      const selectedMatter = formattedMatterData.find(
        (matter) => matter.label === selectedOption
      );

      if (!selectedMatter) return;

      try {
        setIsLoadingClients(true);
        setSelectedMatterUno(selectedMatter.label);
        const response = await fetchClients({
          getAccessToken,
          searchValue: selectedMatter.clientUno,
          office: memoizedOffice,
        });
        const data = response as unknown as ApiResponse<Client>;

        if (data) {
          const formattedData = data.data.items.map(formatClientData);
          setFormattedClientData(formattedData);
          setClientData(formattedData.map((client) => client.clientName));
          setSelectedClientUno(formattedData[0]?.label);
          // Set loading to false when we have data
          setIsLoadingClients(false);
          setShowClientDropdown(false);
          setShowMatterDropdown(false);
        } else {
          setIsLoadingClients(false);
          setShowClientDropdown(false);
          setShowMatterDropdown(false);
        }
      } catch (error) {
        console.error('Error fetching matters:', error);
        setFormattedClientData([]);
        setClientData([]);
        setIsLoadingClients(false);
        setShowClientDropdown(false);
        setShowMatterDropdown(false);
      }
    },
    [formattedMatterData, memoizedOffice, formatClientData, getAccessToken]
  );

  return {
    clientData,
    matterData,
    formattedClientData,
    formattedMatterData,
    selectedClientUno,
    selectedMatterUno,
    selectedClientLabel,
    handleClientSearch,
    handleClientSelect,
    handleMatterSearch,
    handleMatterSelect,
    isLoadingClients,
    setIsLoadingClients,
    isLoadingMatters,
    setIsLoadingMatters,
    showMatterDropdown,
    showClientDropdown,
    setShowClientDropdown,
    setShowMatterDropdown,
  };
};
